import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { LayoutDashboard, FileText, History, TestTube, LogOut, Building2, Inbox, Menu, X, Moon, Sun, ShieldCheck, Users } from 'lucide-react';
import { useState } from 'react';
import { Dashboard } from './pages/Dashboard';
import { WorkflowList } from './pages/WorkflowList';
import { WorkflowEdit } from './pages/WorkflowEdit';
import { Versions } from './pages/Versions';
import { TestPanel } from './pages/TestPanel';
import { Tenants } from './pages/Tenants';
import { TenantForm } from './pages/TenantForm';
import { TenantDetail } from './pages/TenantDetail';
import { Tickets } from './pages/Tickets';
import { TicketDetail } from './pages/TicketDetail';
import { Permissions } from './pages/Permissions';
import { Roles } from './pages/Roles';
import { AuthGate } from './components/AuthGate';
import { api } from './api/client';
import { useTheme } from './hooks/useTheme';

type NavItem = { to: string; icon: React.ElementType; label: string; short: string; superAdminOnly?: boolean };

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',   icon: LayoutDashboard, label: 'Dashboard',       short: 'Home'    },
  { to: '/workflows',   icon: FileText,        label: 'Workflows',        short: 'Flows'   },
  { to: '/tickets',     icon: Inbox,           label: 'Support Tickets',  short: 'Tickets' },
  { to: '/tenants',     icon: Building2,       label: 'Tenants',          short: 'Tenants' },
  { to: '/roles',       icon: Users,            label: 'Roles',            short: 'Roles',  superAdminOnly: true },
  { to: '/permissions', icon: ShieldCheck,     label: 'Permissions',      short: 'Perms',  superAdminOnly: true },
  { to: '/versions',    icon: History,         label: 'Versions',         short: 'Versions'},
  { to: '/test',        icon: TestTube,        label: 'Test Panel',       short: 'Test'    },
];

export default function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const isSuperAdmin = api.isSuperAdmin();
  const visibleNav = NAV_ITEMS.filter((item) => !item.superAdminOnly || isSuperAdmin);

  return (
    <AuthGate>
      <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">

        {/* ── Desktop sidebar ── */}
        <Sidebar theme={theme} toggle={toggle} navItems={visibleNav} />

        {/* ── Mobile drawer overlay ── */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={() => setDrawerOpen(false)}
          >
            <aside
              className="w-64 bg-white dark:bg-gray-800 h-full flex flex-col shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h1 className="text-base font-semibold text-gray-900 dark:text-white">ProMed KB</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Support Agent Admin</p>
                </div>
                <button onClick={() => setDrawerOpen(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1">
                  <X size={20} />
                </button>
              </div>
              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {visibleNav.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <Icon size={18} />
                    {label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-1">
                <button
                  onClick={toggle}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                <button
                  onClick={() => { api.clearToken(); window.location.reload(); }}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <LogOut size={18} /> Sign out
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* ── Content column ── */}
        <div className="flex-1 flex flex-col min-w-0">

          {/* Mobile top bar */}
          <header className="md:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 h-14 flex items-center justify-between flex-shrink-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">ProMed KB</span>
            <div className="flex items-center gap-1">
              <button
                onClick={toggle}
                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                onClick={() => { api.clearToken(); window.location.reload(); }}
                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Sign out"
              >
                <LogOut size={18} />
              </button>
            </div>
          </header>

          {/* Main content — extra bottom padding on mobile for fixed bottom nav */}
          <main className="flex-1 p-4 md:p-8 overflow-auto pb-20 md:pb-8">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard"        element={<Dashboard />} />
              <Route path="/workflows"        element={<WorkflowList />} />
              <Route path="/workflows/new"    element={<WorkflowEdit />} />
              <Route path="/workflows/:id"    element={<WorkflowEdit />} />
              <Route path="/versions"         element={<Versions />} />
              <Route path="/test"             element={<TestPanel />} />
              <Route path="/tenants"          element={<Tenants />} />
              <Route path="/tenants/new"      element={<TenantForm />} />
              <Route path="/tenants/:id"      element={<TenantDetail />} />
              <Route path="/tenants/:id/edit" element={<TenantForm />} />
              <Route path="/tickets"          element={<Tickets />} />
              <Route path="/tickets/:id"      element={<TicketDetail />} />
              <Route path="/roles"            element={<Roles />} />
              <Route path="/permissions"      element={<Permissions />} />
            </Routes>
          </main>
        </div>

        {/* ── Mobile bottom nav ── */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex h-14">
          {visibleNav.map(({ to, icon: Icon, short }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive
                    ? 'text-brand-700 dark:text-brand-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`p-1 rounded-lg transition-colors ${isActive ? 'bg-brand-50 dark:bg-brand-900/30' : ''}`}>
                    <Icon size={18} />
                  </span>
                  {short}
                </>
              )}
            </NavLink>
          ))}
        </nav>

      </div>
    </AuthGate>
  );
}

function Sidebar({ theme, toggle, navItems }: { theme: string; toggle: () => void; navItems: NavItem[] }) {
  function handleSignOut() {
    api.clearToken();
    window.location.reload();
  }

  return (
    <aside className="hidden md:flex w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col flex-shrink-0">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">ProMed KB</h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Support Agent Admin</p>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-1">
        <button
          onClick={toggle}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          {theme === 'dark' ? 'Light mode' : 'Dark mode'}
        </button>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <LogOut size={18} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
